'use client'

/**
 * The org's configured user-menu sections as account-menu items (#15618).
 *
 * The account menu is a Radix `DropdownMenu` whose children must be
 * `menuitem`s for its roving focus and type-ahead to work. Which entries
 * render (`resolveUserMenuLayout` over `buildEntryAvailability`, computed by
 * the caller), each entry's destination, and each action's handler — the
 * command bar, shortcuts drawer, feedback entry, presentation toggle,
 * copy-link and demo-mode handlers — are shared with the sidebar. (The top
 * header's own user menu was deleted by #16727.)
 *
 * Link items close the menu through the account menu's anchor-click handler;
 * action items close it through Radix's own select-and-close.
 */

import type { Route } from 'next'
import Link from 'next/link'
import { Fragment, useContext, type ComponentType, type ReactNode } from 'react'
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@sai/ui/components'

import { AiChatMark } from '@/components/ai-chat/ai-chat-mark'
import {
  copyPageLinkToClipboard,
  entryAction,
  type EntryActionContext,
} from '@/components/entry-actions'
import { FEEDBACK_MODE_EXEMPT_ATTRIBUTE } from '@/components/feedback/feedback-mode-attributes'
import { navEntryFace } from '@/components/header-nav-entries'
import {
  Activity,
  Bell,
  Building2,
  Calendar,
  ClipboardList,
  Eye,
  EyeOff,
  Gauge,
  Keyboard,
  Link2,
  Map as MapIcon,
  Megaphone,
  MessageSquarePlus,
  Play,
  Search,
  Users,
  Zap,
} from '@/components/icons'
import { TerminologyContext } from '@/contexts/terminology-context'
import type { UserMenuLayout } from '@/lib/org-nav/resolve'
import { ENTRIES, type EntryKey } from '@/lib/org-nav/vocabulary'
import {
  BoardsIcon,
  FeedbackIcon,
  HandbookIcon,
  TrainingIcon,
} from '@/lib/ui-icons'

import { trackOrgNavClicked } from './org-nav-tracking'

/** Everything an entry's handler or badge needs. */
export interface AccountMenuEntryContext extends EntryActionContext {
  pendingReviewCount: number
}

type Icon = ComponentType<{ className?: string }>

/**
 * The header user menu's Shift shortcuts, shown in this menu too (#15668):
 * each item sets `aria-keyshortcuts` and draws a `ShortcutHint`.
 */
function shiftShortcut(key: string) {
  return { display: `⇧${key}`, keys: ['Shift', key] }
}

/**
 * A Shift shortcut's visible hint. Not sai-ui's `DropdownMenuShortcut`: that
 * dims `currentColor` with `opacity-60`, which on the account menu's white
 * surface is #858585 — 3.69:1, under WCAG AA's 4.5:1 for 12px text (axe
 * `color-contrast` on every viewer-identity-menu spec). The secondary text
 * token clears it in both themes.
 */
function ShortcutHint({ display }: { display: string }) {
  return (
    <span className="ms-auto text-xs tracking-widest text-text-secondary">
      {display}
    </span>
  )
}

function LinkItem({
  href,
  icon: ItemIcon,
  label,
  trailing,
  onClick,
  shortcut,
}: {
  href: string
  icon: Icon
  label: ReactNode
  trailing?: ReactNode
  onClick?: () => void
  /** A Shift shortcut's key, shown as a hint and set as the link's
   *  `aria-keyshortcuts`. */
  shortcut?: string
}) {
  const hint = shortcut ? shiftShortcut(shortcut) : null
  return (
    <DropdownMenuItem asChild>
      <Link
        href={href as Route}
        onClick={onClick}
        aria-keyshortcuts={hint?.keys.join('+')}
      >
        <ItemIcon />
        <span className="flex-1 truncate">{label}</span>
        {trailing}
        {hint && <ShortcutHint display={hint.display} />}
      </Link>
    </DropdownMenuItem>
  )
}

function ActionItem({
  icon,
  label,
  onSelect,
  disabled,
  exempt,
  shortcut,
}: {
  icon: ReactNode
  label: ReactNode
  onSelect: () => void
  disabled?: boolean
  /** Feedback mode must not anchor feedback to the control that opened it. */
  exempt?: boolean
  /** A Shift shortcut's key, shown as a hint. */
  shortcut?: string
}) {
  const props = {
    onSelect,
    disabled,
    ...(exempt ? { [FEEDBACK_MODE_EXEMPT_ATTRIBUTE]: 'true' } : {}),
  }
  // Not `DropdownMenuShortcutItem`: its hint is the low-contrast one
  // `ShortcutHint` replaces, so the item carries the attribute itself.
  const hint = shortcut ? shiftShortcut(shortcut) : null
  return (
    <DropdownMenuItem {...props} aria-keyshortcuts={hint?.keys.join('+')}>
      {icon}
      <span className="flex-1 truncate">{label}</span>
      {hint && <ShortcutHint display={hint.display} />}
    </DropdownMenuItem>
  )
}

function CountBadge({ count }: { count: number }) {
  if (count <= 0) return null
  return (
    <span className="rounded-full bg-fill-10 px-1.5 text-xs font-semibold tabular-nums text-text-secondary">
      {count}
    </span>
  )
}

/** One configurable entry as a menu item. */
export function AccountMenuEntry({
  itemKey,
  ctx,
}: {
  itemKey: EntryKey
  ctx: AccountMenuEntryContext
}) {
  const terminology = useContext(TerminologyContext)

  switch (itemKey) {
    case 'ai-chat':
      return (
        <ActionItem
          icon={<AiChatMark size={16} />}
          label="AI Chat"
          shortcut="A"
          onSelect={() => window.dispatchEvent(new CustomEvent('ai-chat:open'))}
        />
      )
    case 'command-bar':
      return (
        <ActionItem
          icon={<Search />}
          label="Command Bar"
          shortcut="K"
          onSelect={ctx.openCommandBar}
        />
      )
    // `pulse` — the menu's former `pulse-check` row (#15689 folded the key).
    case 'pulse':
      return (
        <LinkItem
          href="/pulse"
          icon={terminology?.getIcon('pulseCheck') ?? Activity}
          label={terminology?.getLabel('pulseCheck') ?? 'Pulse Check'}
        />
      )
    case 'notifications':
      return (
        <LinkItem
          href="/notifications"
          icon={Bell}
          label="Notifications"
          shortcut="N"
        />
      )
    case 'feedback':
      return (
        <>
          <ActionItem
            icon={<MessageSquarePlus />}
            label="Send Feedback"
            shortcut="F"
            onSelect={ctx.openFeedback}
            exempt
          />
          <LinkItem href="/feedback" icon={FeedbackIcon} label="My Feedback" />
        </>
      )
    case 'orgs':
      return (
        <LinkItem
          href="/handbook"
          icon={terminology?.getIcon('handbook') ?? HandbookIcon}
          label={terminology?.getLabel('handbook') ?? 'Handbook'}
          onClick={() => void trackOrgNavClicked()}
        />
      )
    case 'people':
      return (
        <LinkItem
          href="/people"
          icon={Users}
          label="People"
          trailing={<CountBadge count={ctx.pendingReviewCount} />}
        />
      )
    case 'meetings':
      return <LinkItem href="/meetings" icon={Calendar} label="Meetings" />
    case 'performance':
      return <LinkItem href="/performance" icon={Gauge} label="Performance" />
    case 'plans':
      return <LinkItem href="/plans" icon={MapIcon} label="Plans" />
    case 'boards':
      return <LinkItem href="/boards" icon={BoardsIcon} label="Boards" />
    case 'company-info':
      return (
        <LinkItem href="/company-info" icon={Building2} label="Company Info" />
      )
    case 'presentation-mode':
      return (
        <ActionItem
          icon={<EyeOff />}
          label={
            ctx.isPresentationMode
              ? 'Exit Presentation Mode'
              : 'Presentation Mode'
          }
          shortcut="S"
          onSelect={ctx.togglePresentationMode}
        />
      )
    case 'copy-page-link':
      return (
        <ActionItem
          icon={<Link2 />}
          label="Copy Page Link"
          shortcut="L"
          onSelect={copyPageLinkToClipboard}
        />
      )
    case 'keyboard-shortcuts':
      return (
        <ActionItem
          icon={<Keyboard />}
          label="Keyboard Shortcuts"
          shortcut="?"
          onSelect={ctx.openShortcuts}
        />
      )
    case 'training':
      return <LinkItem href="/training" icon={TrainingIcon} label="Training" />
    case 'whats-new':
      return <LinkItem href="/whats-new" icon={Megaphone} label="What’s New" />
    case 'surveys':
      return (
        <LinkItem
          href="/surveys"
          icon={terminology?.getIcon('survey') ?? ClipboardList}
          label={terminology?.getLabel('survey', true) ?? 'Surveys'}
        />
      )
    case 'ai-integrations':
      return (
        <LinkItem href="/ai-integrations" icon={Zap} label="AI Integrations" />
      )
    case 'demo-mode':
      return ctx.isInDemoMode ? (
        <ActionItem
          icon={ctx.isDemoBarVisible ? <EyeOff /> : <Eye />}
          label={ctx.isDemoBarVisible ? 'Hide demo bar' : 'Show demo bar'}
          onSelect={ctx.toggleDemoBar}
        />
      ) : (
        <ActionItem
          icon={<Play />}
          label={ctx.isEnteringDemo ? 'Entering...' : 'Demo Mode'}
          onSelect={ctx.onEnterDemoMode}
          disabled={ctx.isEnteringDemo}
        />
      )
    default: {
      // #15689 — an entry with no hand-written item: an action as an action
      // item, a page as a link item, each with its Shift shortcut.
      const action = entryAction(itemKey, ctx)
      if (action) {
        return (
          <ActionItem
            icon={<action.Icon />}
            label={action.label}
            shortcut={action.shortcut}
            onSelect={action.run}
            disabled={action.disabled}
            exempt={action.feedbackExempt}
          />
        )
      }
      const face = navEntryFace(itemKey, terminology)
      return (
        <LinkItem
          href={face.href}
          icon={face.Icon}
          label={face.label}
          shortcut={ENTRIES[itemKey].shortcut}
        />
      )
    }
  }
}

/**
 * Each resolved section behind a separator, its title (when it has one) as a
 * `DropdownMenuLabel`, then its entries — the header menu's section frame,
 * spelled as menu items. Empty sections are already dropped by the resolver.
 */
export function AccountMenuSections({
  layout,
  ctx,
}: {
  layout: UserMenuLayout
  ctx: AccountMenuEntryContext
}) {
  return layout.sections.map((section, index) => (
    // Sections have no identity beyond their position; titles may repeat.
    <Fragment key={index}>
      <DropdownMenuSeparator />
      {section.title && (
        <DropdownMenuLabel className="text-xs uppercase tracking-wider text-text-secondary">
          {section.title}
        </DropdownMenuLabel>
      )}
      {section.items.map(key => (
        <AccountMenuEntry key={key} itemKey={key} ctx={ctx} />
      ))}
    </Fragment>
  ))
}
